import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import basicKitty from '../assets/basic-kitty.png';
import config from '../config';

const Admin = () => {
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [activeNav, setActiveNav] = useState(3);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    // Check for auth token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const authStatus = urlParams.get('auth');
    
    if (token && authStatus === 'success') {
      // Store token in localStorage
      localStorage.setItem('authToken', token);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    checkAuthStatus();
    fetchPhotos();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      let url = `${config.API_URL}/auth/user`;
      const options = { credentials: 'include' };
      
      if (token) {
        // Add token as query parameter or header
        url += `?token=${token}`;
        // Or alternatively use header:
        // options.headers = { 'Authorization': `Bearer ${token}` };
      }
      
      const response = await fetch(url, options);
      const data = await response.json();
      if (data.authenticated) {
        setUser(data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/photos`);
      const data = await response.json();
      setPhotos(data);
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const handleLogin = () => {
    window.location.href = `${config.API_URL}/auth/google`;
  };

  const handleLogout = async () => {
    try {
      await fetch(`${config.API_URL}/auth/logout`, { credentials: 'include' });
      // Clear token from localStorage
      localStorage.removeItem('authToken');
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadSuccess('');

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('description', description);
        formData.append('tags', tags);

        const response = await fetch(`${config.API_URL}/api/upload`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error);
        }
      }

      setUploadSuccess(`${files.length} photo(s) uploaded successfully!`);
      setDescription('');
      setTags('');
      fetchPhotos();
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const files = Array.from(event.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDeletePhoto = async (photoId, originalName) => {
    if (!window.confirm(`Are you sure you want to delete "${originalName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchPhotos();
        alert('Photo deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Delete failed: ${error.error}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed. Please try again.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 25%, #fdf2f8 50%, #f1f5f9 75%, #fdf4ff 100%)',
      fontFamily: "'Playfair Display', 'Georgia', serif",
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Floating background elements */}
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 6 + 4 + 'px',
            height: Math.random() * 6 + 4 + 'px',
            borderRadius: '50%',
            background: `linear-gradient(45deg, 
              rgba(219, 39, 119, ${0.1 + Math.random() * 0.3}), 
              rgba(168, 85, 247, ${0.1 + Math.random() * 0.3}))`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `admin-float ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: Math.random() * 2 + 's',
          }}
        />
      ))}

      {/* Navigation Bar */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        zIndex: 1000,
        padding: '1rem 2rem',
        borderBottom: '1px solid rgba(219, 39, 119, 0.1)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <img 
              src={basicKitty} 
              alt="Kitty Logo" 
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #db2777',
                boxShadow: '0 2px 8px rgba(219, 39, 119, 0.2)'
              }} 
            />
            <div style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px'
            }}>MC</div>
          </div>
          <div style={{ display: 'flex', gap: '2rem' }}>
            {[
              { name: 'Home', path: '/' },
              { name: 'Gallery', path: '/gallery' },
              { name: 'Career', path: '/career' },
              { name: 'Admin', path: '/admin' }
            ].map((item, index) => (
              <Link 
                key={item.name} 
                to={item.path}
                style={{
                  cursor: 'pointer',
                  fontWeight: '500',
                  color: activeNav === index ? '#db2777' : '#64748b',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  padding: '0.5rem 0',
                  textDecoration: 'none'
                }}
                onMouseEnter={() => setActiveNav(index)}
                onMouseLeave={() => setActiveNav(3)}
              >
                {item.name}
                {activeNav === index && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    height: '2px',
                    background: 'linear-gradient(90deg, #db2777, #ec4899)',
                    borderRadius: '1px'
                  }} />
                )}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section style={{
        padding: '8rem 2rem 3rem 2rem',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4rem)',
            fontWeight: '700',
            margin: '0',
            background: 'linear-gradient(45deg, #db2777, #a855f7, #ec4899)',
            backgroundSize: '200% 200%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'gradient-shift 4s ease-in-out infinite'
          }}>Admin Panel</h1>
          <p style={{
            fontSize: '1.3rem',
            color: '#64748b',
            fontStyle: 'italic',
            margin: '0.5rem 0 0 0'
          }}>Upload and Manage Photos</p>
        </div>
      </section>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 2rem 4rem 2rem',
        position: 'relative',
        zIndex: 10
      }}>
        {!user ? (
          <div style={{
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '2rem',
            padding: '4rem 2rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(219, 39, 119, 0.1)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '2rem' }}>🔐</div>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '1rem'
            }}>Admin Access Required</h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#64748b',
              marginBottom: '2rem',
              lineHeight: 1.6
            }}>Please sign in with your authorized Google account to access the admin panel.</p>
            <button 
              onClick={handleLogin}
              style={{
                padding: '1rem 2rem',
                background: 'linear-gradient(45deg, #4285f4, #34a853)',
                color: 'white',
                border: 'none',
                borderRadius: '1rem',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '600',
                boxShadow: '0 8px 25px rgba(66, 133, 244, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 35px rgba(66, 133, 244, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(66, 133, 244, 0.3)';
              }}
            >
              🔑 Sign in with Google
            </button>
          </div>
        ) : (
          <>
            {/* Welcome Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '1.5rem',
              padding: '2rem',
              marginBottom: '3rem',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(219, 39, 119, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img 
                  src={user.photo} 
                  alt={user.name}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #db2777'
                  }}
                />
                <div>
                  <h3 style={{
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0'
                  }}>Welcome back, {user.name}! 👋</h3>
                  <p style={{
                    color: '#64748b',
                    margin: '0.2rem 0 0 0',
                    fontSize: '0.95rem'
                  }}>{user.email}</p>
                </div>
              </div>
              <button 
                onClick={handleLogout}
                style={{
                  padding: '0.7rem 1.5rem',
                  background: 'linear-gradient(45deg, #dc3545, #e74c3c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.8rem',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                🚪 Logout
              </button>
            </div>

            {/* Upload Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '1.5rem',
              padding: '2rem',
              marginBottom: '3rem',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(219, 39, 119, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>📷 Upload New Photos</h3>

              {/* Metadata Inputs */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>Description (optional)</label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Add a description for your photos..."
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.8rem',
                      fontSize: '0.95rem',
                      transition: 'border-color 0.3s ease',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#db2777';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>Tags (optional)</label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="portrait, nature, event (comma separated)"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '0.8rem',
                      fontSize: '0.95rem',
                      transition: 'border-color 0.3s ease',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#db2777';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                </div>
              </div>

              {/* Drag and Drop Upload */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                  border: `3px dashed ${dragOver ? '#db2777' : '#d1d5db'}`,
                  borderRadius: '1rem',
                  padding: '3rem 2rem',
                  textAlign: 'center',
                  background: dragOver ? 'rgba(219, 39, 119, 0.05)' : 'rgba(249, 250, 251, 0.5)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
                  {uploading ? '⏳' : dragOver ? '📁' : '📸'}
                </div>
                <h4 style={{
                  fontSize: '1.2rem',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 0.5rem 0'
                }}>
                  {uploading ? 'Uploading...' : 'Drop photos here or click to browse'}
                </h4>
                <p style={{
                  color: '#64748b',
                  margin: '0',
                  fontSize: '0.95rem'
                }}>
                  Supports JPG, PNG, WEBP files • Multiple files allowed
                </p>
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </div>

              {uploadSuccess && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '0.8rem',
                  color: '#065f46',
                  textAlign: 'center',
                  fontWeight: '500'
                }}>
                  ✅ {uploadSuccess}
                </div>
              )}
            </div>

            {/* Photo Management Section */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '1.5rem',
              padding: '2rem',
              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(219, 39, 119, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>🗂️ Manage Photos ({photos.length})</h3>

              {photos.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                  <p style={{ fontSize: '1.1rem', margin: 0 }}>No photos uploaded yet</p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '1rem'
                }}>
                  {photos.map((photo) => (
                    <div key={photo._id} style={{
                      position: 'relative',
                      background: 'white',
                      borderRadius: '0.8rem',
                      overflow: 'hidden',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.3s ease',
                      border: '1px solid rgba(219, 39, 119, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    >
                      <img 
                        src={photo.s3Url} 
                        alt={photo.originalName}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover'
                        }}
                      />
                      <button
                        onClick={() => handleDeletePhoto(photo._id, photo.originalName)}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          background: 'rgba(220, 53, 69, 0.9)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(220, 53, 69, 1)';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(220, 53, 69, 0.9)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        ×
                      </button>
                      <div style={{ padding: '0.8rem' }}>
                        <p style={{
                          fontSize: '0.8rem',
                          color: '#64748b',
                          margin: '0',
                          fontWeight: '500',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {photo.originalName}
                        </p>
                        <p style={{
                          fontSize: '0.7rem',
                          color: '#9ca3af',
                          margin: '0.2rem 0 0 0'
                        }}>
                          {new Date(photo.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
        
        @keyframes admin-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(180deg); }
        }
        
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default Admin;